import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BigCards from '../../components/BigCards/BigCards';
import RangeContainer from '../../components/RangeContainer/RangeContainer';
import { useGame } from '../../context/GameContext';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';

import './answer-screen.css';

const CARD_VALUES = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];

const AnswerScreen = () => {
  const [range, setRange] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { handleContinue } = useGame();
  
  // Create a base state with defaults
  const defaultState = {
    heroPosition: 'BTN',
    betType: 'FACING_3BET_4BET',
    villainPosition: '',
    actionTaken: 'fold',
    frequency: 0,
    actualFrequency: 0,
    isCorrect: false
  };
  
  // Merge with location state
  const state = {
    ...defaultState,
    ...(location.state || {})
  };
  
  // If there's no selectedHand in the state, use a default
  if (!state.selectedHand || !state.selectedHand.card1 || !state.selectedHand.card2) {
    state.selectedHand = { card1: 'Ah', card2: 'Ad' };
  }

  // Load the range data once
  useEffect(() => {
    const loadRange = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Determine file path based on state
        const position = state.heroPosition.toLowerCase();
        
        // Generate correct filepath based on bet type
        let filePath;
        if (state.betType === 'FACING_3BET_4BET') {
          const threeBetPos = state.config?.threeBetPosition?.toLowerCase() || 'sb';
          filePath = `/database/preflop/ranges/${position}/${position}_4bet-${threeBetPos}_3bet.csv`;
        } else if (state.betType === 'FACING_OPEN_3BET') {
          const openPos = state.config?.openPosition?.toLowerCase() || 'hj';
          filePath = `/database/preflop/ranges/${position}/${position}_3bet-${openPos}_open.csv`;
        } else if (state.betType === 'OPEN') {
          filePath = `/database/preflop/ranges/${position}/${position}_open.csv`;
        } else {
          // Handle other bet types appropriately
          console.log('Using specific range path from config:', state.config?.range);
          filePath = state.config?.range || `/database/preflop/ranges/${position}/${position}_open.csv`;
        }
        
        console.log('AnswerScreen: Loading range from file:', filePath);
        
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to load range file (${response.status})`);
        }

        const text = await response.text();
        const lines = text.trim().split('\n');
        
        const hands = [];
        // Skip header row if it exists
        const startIndex = lines[0].toLowerCase().includes('hand') ? 1 : 0;
        
        for (let i = startIndex; i < lines.length; i++) {
          const parts = lines[i].split(',').map(v => v.trim());
          if (parts.length < 2) continue;
          
          const hand = parts[0];
          const type = parts.length > 1 ? parts[1] : '';
          const freq = parts.length > 2 ? parts[2] : '0';
          const frequency = parseInt(freq.replace('%', ''));
          
          if (!hand || isNaN(frequency)) continue;

          if (hand[0] === hand[1]) {
            hands.push({
              card1: hand[0] + 'h',
              card2: hand[0] + 'd',
              frequency
            });
          } else if (type === 'suited') {
            hands.push({
              card1: hand[0] + 'h',
              card2: hand[1] + 'h',
              frequency
            });
          } else {
            hands.push({
              card1: hand[0] + 'h',
              card2: hand[1] + 'd',
              frequency
            });
          }
        }

        setRange(hands);
        setError(null);
      } catch (err) {
        console.error('Error loading range:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRange();
  }, []); // Empty dependency array - only load once when component mounts

  const getAnswerSubtitle = () => {
    // Use actualFrequency from state if available, otherwise fall back to frequency
    const frequency = state.actualFrequency !== undefined ? state.actualFrequency : state.frequency;
    const action = state.actionTaken;
    const isCorrect = state.isCorrect;

    if (isCorrect) {
      return 'Stack those chips!';
    }
    
    // When frequency is 100 and they folded = too tight
    if (frequency === 100 && action === 'fold') {
      return 'Relax, open up a little.';
    }
    
    // When frequency is 0 and they didn't fold = too loose
    if (frequency === 0 && action !== 'fold') {
      return 'A little loosey-goosey';
    }

    return 'A little loosey-goosey'; // Default fallback
  };

  const getRangeText = (selectedHand, betType, range) => {
    if (!selectedHand || !range?.length) return '';
    
    const value1 = selectedHand.card1[0];
    const value2 = selectedHand.card2[0];
    const suited = selectedHand.card1[1] === selectedHand.card2[1];
    
    const handType = value1 === value2 ? 'pair' : (suited ? 'suited' : 'offsuit');
    
    // Determine action type display text dynamically based on bet type
    let actionTypeDisplay = '';
    
    if (state.betType === 'FACING_3BET_4BET') {
      actionTypeDisplay = '4BET';
    } else if (state.betType === 'OPEN') {
      actionTypeDisplay = 'OPEN';
    } else if (state.betType === 'FACING_OPEN_CALL') {
      actionTypeDisplay = 'CALL';
    } else if (state.betType === 'FACING_OPEN_3BET') {
      actionTypeDisplay = '3BET';
    } else if (state.betType === 'FACING_3BET_CALL') {
      actionTypeDisplay = 'CALL';
    } else if (state.betType === 'FACING_4BET_CALL') {
      actionTypeDisplay = 'CALL';
    } else if (state.betType === 'SQUEEZE_4BET') {
      actionTypeDisplay = '4BET';
    } else if (state.betType === 'CALL_VS_3BET') {
      actionTypeDisplay = 'CALL';
    } else {
      // Fallback to uppercase bet type if not recognized
      actionTypeDisplay = betType.toUpperCase();
    }
    
    // Use actualFrequency from state if available, otherwise fall back to frequency
    const displayFrequency = state.actualFrequency !== undefined ? state.actualFrequency : state.frequency;
    
    // Build the text dynamically based on the hand type
    if (handType === 'pair') {
      return `Other pairs with ${displayFrequency}% ${actionTypeDisplay}`;
    }
    
    if (handType === 'suited') {
      return `Other ${value1}x suited hands with ${displayFrequency}% ${actionTypeDisplay}`;
    }
    
    return `Other ${value1}x offsuit hands with ${displayFrequency}% ${actionTypeDisplay}`;
  };

  if (loading) {
    return <div className="answer-screen">Loading range data...</div>;
  }

  if (error) {
    return (
      <div className="answer-screen">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Use actualFrequency from state if available, otherwise fall back to frequency
  const displayFrequency = state.actualFrequency !== undefined ? state.actualFrequency : state.frequency;
  
  // Determine action type display text dynamically for frequency display
  let actionTypeDisplay = '';
  if (state.betType === 'FACING_3BET_4BET') {
    actionTypeDisplay = '4BET';
  } else if (state.betType === 'OPEN') {
    actionTypeDisplay = 'OPEN';
  } else if (state.betType === 'FACING_OPEN_CALL') {
    actionTypeDisplay = 'CALL';
  } else if (state.betType === 'FACING_OPEN_3BET') {
    actionTypeDisplay = '3BET';
  } else if (state.betType === 'FACING_3BET_CALL') {
    actionTypeDisplay = 'CALL';
  } else if (state.betType === 'FACING_4BET_CALL') {
    actionTypeDisplay = 'CALL';
  } else if (state.betType === 'SQUEEZE_4BET') {
    actionTypeDisplay = '4BET';
  } else if (state.betType === 'CALL_VS_3BET') {
    actionTypeDisplay = 'CALL';
  } else {
    // Fallback to uppercase bet type if not recognized
    actionTypeDisplay = state.betType.toUpperCase();
  }

  return (
    <div className="answer-screen">
      <div className="answer-container">
        <div className="result-background">
          <img 
            src={`/svgs/${state.isCorrect ? 'correct' : 'incorrect'}.svg`} 
            alt={state.isCorrect ? 'Correct answer' : 'Incorrect answer'}
            className="result-svg"
          />
          <div className="cards-and-text">
            <BigCards selectedHand={state.selectedHand} />
            <div className="frequency-text">
              {displayFrequency}% {actionTypeDisplay}
            </div>
          </div>
        </div>
        
        <h1 className="answer-title">
          {state.isCorrect ? 'Correct' : 'Incorrect'}
        </h1>
        <p className="answer-subtitle">
          {getAnswerSubtitle()}
        </p>
        
        <p className="range-text">
          {getRangeText(state.selectedHand, state.betType, range)}
        </p>

        <div className="range-container">
          <RangeContainer 
            selectedHand={state.selectedHand}
            range={range}
            cardValues={CARD_VALUES}
          />
        </div>
        
        <div className="answer-button-container">
          <ButtonLarge
            text="View Range"
            buttonLargeClassName="button-large view-range-button"
            onClick={() => navigate('/view-range', { 
              state: { 
                ...state,
                config: {
                  ...state.config,
                  heroPosition: state.heroPosition,
                  betType: state.betType
                },
                returnTo: '/answer'
              }
            })}
          />
          <ButtonLarge
            text="Continue"
            buttonLargeClassName="button-large continue-button"
            onClick={() => {
              console.log('AnswerScreen: Continue button clicked, using handleContinue');
              // Use the provided handleContinue from GameContext
              // This will properly manage the quiz state
              handleContinue();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AnswerScreen;